'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import UIAutosizedTextInput from 'UIComponents/input/UIAutosizedTextInput';
import ValueBuffer from 'DesignData/lib/ValueBuffer';
import { getCodemirrorOptions } from 'DesignData/selectors/codemirror';
import { getFlash } from 'DesignData/selectors/flash';
import { KEYS } from 'DesignData/enums/Keys';
import { LANGUAGE_EDITOR_LANGUAGE_REGENERATED } from 'DesignData/enums/FlashKeys';
const InlineInput = styled(UIAutosizedTextInput).withConfig({
  displayName: "withDescriptionInputs__InlineInput",
  componentId: "sc-8yxtgb-0"
})(["max-width:calc(100% - (170px + (", " * 6)));> span{font-size:", " !important;}"], props => props.fontSize, props => props.fontSize);
const START_CHARACTER = 20;
/* Match only the start of the string */

const DESCRIPTION_REGEX = /^ {4}"description": /gm;
const VALUE_REGEX = / {4}"description": "(.*)"/m;

const generateLineProps = stream => {
  return {
    line: stream.lineOracle.line,
    value: VALUE_REGEX.exec(stream.string)[1],
    ch: stream.string.length - 2
  };
};

const mapStateToProps = state => {
  return {
    codemirrorOptions: getCodemirrorOptions(state),
    refresh: getFlash(state, {
      key: LANGUAGE_EDITOR_LANGUAGE_REGENERATED
    })
  };
};

const mapDispatchToProps = {};
export default (IDECodeMirror => {
  class WithDescriptionInputs extends PureComponent {
    constructor(props) {
      super(props);
      this.linesHash = {};

      this.onBlur = (line, evt) => {
        const {
          target: {
            value
          }
        } = evt;
        if (value === line.value) return;
        /* We need to clear and regenerate the widget with the line line info */

        const widget = this.linesHash[line.line];
        if (!widget) return;
        this.cm.operation(() => {
          widget.clear();
          this.cm.doc.replaceRange(value, {
            line: line.line,
            ch: START_CHARACTER
          }, {
            line: line.line,
            ch: line.ch
          });
          const newLine = {
            line: line.line,
            ch: this.cm.doc.getLine(line.line).length - 2,
            value
          };
          this.addWidget(newLine);
        });
      };

      this.onKeyPress = evt => {
        if (evt.key === KEYS.ENTER || evt.key === 'Enter') {
          evt.target.blur();
        }
      };

      this.onBufferBroadcast = buffer => {
        if (buffer.length) {
          this.cm.operation(() => {
            buffer.forEach(line => {
              this.addWidget(line);
            });
          });
        }
      };

      this.addWidget = line => {
        const node = this.createNode(line);
        this.linesHash[line.line] = this.cm.markText({
          line: line.line,
          ch: START_CHARACTER
        }, {
          line: line.line,
          ch: line.ch
        }, {
          replacedWith: node,
          clearWhenEmpty: false,
          inclusiveLeft: true,
          inclusiveRight: true
        });
      };

      this.createNode = line => {
        const container = document.createElement('span');
        ReactDOM.render( /*#__PURE__*/_jsx(InlineInput, {
          defaultValue: line.value,
          use: "on-dark",
          affordance: true,
          fontSize: this.props.codemirrorOptions.fontSize,
          onBlur: this.props.partial(this.onBlur, line),
          onKeyPress: this.onKeyPress
        }), container);
        return container;
      };

      this.createOverlay = () => {
        return {
          token: stream => {
            const match = DESCRIPTION_REGEX.exec(stream.string);

            if (match) {
              const lineNumber = stream.lineOracle.line;

              if (!this.linesHash[lineNumber]) {
                const lineProps = generateLineProps(stream);
                this.linesHash[lineNumber] = lineNumber;
                this.lineBuffer.next(lineProps);
              }
            }

            stream.skipToEnd();
            return null;
          }
        };
      };

      this.codemirrorDidMountOrUpdate = codemirror => {
        if (this.cm === codemirror) return;
        this.cm = codemirror;
        if (!this.cm) return;

        if (!this.cm_overlay) {
          this.cm_overlay = this.createOverlay();
          this.cm.addOverlay(this.cm_overlay);
        }
      };

      this.setRef = ref => {
        /* Don't do anything if the file is not readonly */
        if (!this.props.readOnly || !!this.props.revisionId) return;
        const codemirror = ref && ref.codemirror;
        this.codemirrorDidMountOrUpdate(codemirror);
      };

      this.lineBuffer = new ValueBuffer(this.onBufferBroadcast);
    }

    componentDidUpdate(prevProps) {
      if (prevProps.refresh && this.cm) {
        this.linesHash = {};
        /* We need to remove and add the overlay again in order to get it to run again */

        if (this.cm_overlay) {
          this.cm.removeOverlay(this.cm_overlay);
        } else {
          this.cm_overlay = this.createOverlay();
        }

        this.cm.addOverlay(this.cm_overlay);
      }
    }

    render() {
      return /*#__PURE__*/_jsx(IDECodeMirror, Object.assign({}, this.props, {
        refCodemirror: this.setRef
      }));
    }

  }

  WithDescriptionInputs.propTypes = {
    value: PropTypes.string.isRequired,
    codemirrorOptions: PropTypes.object.isRequired,
    partial: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    refresh: PropTypes.bool,
    revisionId: PropTypes.number
  };
  WithDescriptionInputs.defaultProps = {
    value: ''
  };
  return connect(mapStateToProps, mapDispatchToProps)(WithDescriptionInputs);
});